import {
  Children,
  type FC,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import { AppRoute } from '@/common/app/routes';
import { useArenaOffersTimer } from '@/common/hooks/arena-offers-timer';
import { useMatchPath } from '@/common/hooks/match-path';
import { type ArenaOffersName } from '@/common/types/shop/marketBoxes';
import { useDailyGoodsLink } from '@/components/Home/DailyGoodsLink/daily-goods-link.hook';
import { DailyGoodsLink } from '@/components/Home/DailyGoodsLink/DailyGoodsLink';
import HelpIcon from '@/components/Modals/HelpModal/HelpIcon';
import { ReadMeTool } from '@/components/ReadMeTool/ReadMeTool';
import { selectAvailableProfitDrops } from '@/redux/selectors/shopSelector';
import { useAppSelector } from '@/redux/store';

import { BingoEventBadge } from '../BingoEventBadge/BingoEventBadge';
import { FriendsLink } from '../FriendsLink/FriendsLink';
import { LotteryHistoryNavLink } from '../LotteryHistoryNavLink/LotteryHistoryNavLink';
import { LotteryRulesNavLink } from '../LotteryRulesNavLink/LotteryRulesNavLink';
import OfferDiscountBadge from '../OfferDiscountBadge/OfferDiscountBadge';

import { getOffsetByPage } from './config';

import css from './styles.module.scss';

const weekendOffers: ArenaOffersName[] = [
  'growthKitGiant',
  'superStart',
  'growthKitSmall',
  'growthKitBig',
];

const startOffers: ArenaOffersName[] = ['battleStart'];

const ITEMS_PER_COLUMN = 3;

const countRenderable = (node: ReactNode): number =>
  Children.toArray(node).filter(Boolean).length;

const Slot: FC<{ id: string; children: ReactNode }> = ({ id, children }) => (
  <div id={id} className="has-[*]:mb-2">
    {children}
  </div>
);

const ColumnStack: FC<{ children: ReactNode }> = ({ children }) => {
  const columns: ReactNode[][] = [];
  let current: ReactNode[] = [];
  let load = 0;

  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    const slotChildren = (child as ReactElement<{ children?: ReactNode }>).props
      .children;
    const weight = countRenderable(slotChildren);

    if (weight > 0 && load + weight > ITEMS_PER_COLUMN && current.length > 0) {
      columns.push(current);
      current = [];
      load = 0;
    }
    current.push(child);
    load += weight;
  }

  if (current.length > 0) columns.push(current);

  return (
    <div className="flex flex-row-reverse items-start gap-2 pointer-events-none">
      {columns.map((col, idx) => (
        <div key={idx} className="flex flex-col items-end">
          {col}
        </div>
      ))}
    </div>
  );
};

const ToolBar: FC = () => {
  const location = useLocation();
  useArenaOffersTimer();
  const isOnHome = !!useMatchPath([AppRoute.MAIN]);
  const { isVisible: isDailyGoodsActive } = useDailyGoodsLink();

  const isHelpIconinVisible = useMatchPath([
    AppRoute.SETTINGS,
    `${AppRoute.SETTINGS}/changeLanguage`,
    `${AppRoute.SETTINGS}/changeSkin`,
    AppRoute.LEAGUES,
    AppRoute.ERRORPAGE,
    AppRoute.SHOP,
    AppRoute.DICE_GAME,
    AppRoute.WORKSHOP,
    AppRoute.BINGO,
    AppRoute.BINGO_LEADERBOARD,
  ]);
  const arenaOffersWithDiscount = useAppSelector((state) =>
    state.shop.arenaOffers.filter(
      (offer) => offer.cost.has_discount && offer.discountTimeLeftInSeconds > 0,
    ),
  );
  const profitDropOffers = useAppSelector(selectAvailableProfitDrops);
  const bingoEvent = useAppSelector((state) => state.shop.bingoEvent);
  const startOffer = arenaOffersWithDiscount.find((offer) =>
    startOffers.includes(offer.name),
  );
  const weekendOffer = arenaOffersWithDiscount.find((offer) =>
    weekendOffers.includes(offer.name),
  );
  const arenaOffer = arenaOffersWithDiscount.find(
    (offer) =>
      !weekendOffers.includes(offer.name) && !startOffers.includes(offer.name),
  );

  const profitDropDiscount = profitDropOffers.find(
    (offer) => offer.cost.has_discount && offer.discountTimeLeftInSeconds > 0,
  );

  const offerItems = isOnHome
    ? (
        [
          {
            offer: startOffer,
            type: 'start',
          },
          {
            offer: weekendOffer,
            type: 'weekend',
          },
          {
            offer: arenaOffer,
            type: 'arena',
          },
          {
            offer: profitDropDiscount,
            type: 'profitDrop',
          },
        ] as const
      ).filter((item) => !!item.offer)
    : [];

  const helpToolIsExist = !isHelpIconinVisible;
  const isOnLottery = location.pathname === AppRoute.LOTTERY;
  const isBingoActive =
    isOnHome &&
    !!bingoEvent &&
    (bingoEvent.endsInSeconds > 0 ||
      (bingoEvent.phase === 'ratingWindow' &&
        (bingoEvent.ratingClosesInSeconds ?? 0) > 0));

  return (
    <div style={{ top: getOffsetByPage(location) }} className={css.toolBar}>
      <div className="flex flex-col items-start pointer-events-none">
        <div id="toolbar-item-1" className="has-[*]:mb-2">
          {helpToolIsExist && <HelpIcon />}
          {isOnLottery && (
            <LotteryHistoryNavLink className="pointer-events-auto" />
          )}
        </div>
        <div id="toolbar-item-3" className="has-[*]:mb-2">
          {<ReadMeTool />}
        </div>
        <div id="toolbar-item-5" className="has-[*]:mb-2">
          <FriendsLink />
        </div>
      </div>
      <ColumnStack>
        <Slot id="toolbar-item-2">
          {offerItems[0] && (
            <OfferDiscountBadge
              arenaOffer={offerItems[0].offer!}
              type={offerItems[0].type}
            />
          )}
          {isOnLottery && (
            <LotteryRulesNavLink className="pointer-events-auto" />
          )}
        </Slot>
        <Slot id="toolbar-item-4">
          {offerItems[1] && (
            <OfferDiscountBadge
              arenaOffer={offerItems[1].offer!}
              type={offerItems[1].type}
            />
          )}
        </Slot>
        <Slot id="toolbar-item-6">
          {isOnHome && isDailyGoodsActive && <DailyGoodsLink />}
          {offerItems[2] && (
            <OfferDiscountBadge
              arenaOffer={offerItems[2].offer!}
              type={offerItems[2].type}
            />
          )}
        </Slot>
        <Slot id="toolbar-item-7">
          {offerItems[3] && (
            <OfferDiscountBadge
              arenaOffer={offerItems[3].offer!}
              type={offerItems[3].type}
            />
          )}
          {isBingoActive && <BingoEventBadge />}
        </Slot>
      </ColumnStack>
    </div>
  );
};

export { ToolBar };
